import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, X, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

function ChatHeader({ onClose }) {
  return (
    <div className="bg-navy text-white px-4 py-3 flex items-center justify-between">
      <div>
        <h3 className="font-heading font-semibold">Live Chat</h3>
        <p className="text-xs text-gray-300">We typically reply within minutes</p>
      </div>
      <Button variant="ghost" size="icon" onClick={onClose} className="text-white hover:bg-white/20">
        <X className="h-4 w-4" />
      </Button>
    </div>
  );
}

function ChatMessages({ messages, loading, messagesEndRef }) {
  if (messages.length === 0) {
    return (
      <div className="h-80 overflow-y-auto p-4 bg-gray-50 flex items-center justify-center">
        <div className="text-center text-gray-500 text-sm">
          <MessageCircle className="h-8 w-8 mx-auto mb-2 text-gray-300" />
          <p>Start a conversation!</p>
          <p className="text-xs mt-1">{"We're here to help with your printing needs."}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-80 overflow-y-auto p-4 space-y-3 bg-gray-50">
      {messages.map((msg) => (
        <div key={msg.id} className={`flex ${msg.is_from_user ? 'justify-end' : 'justify-start'}`}>
          <div className={`max-w-[80%] px-4 py-2 rounded-2xl text-sm ${
            msg.is_from_user
              ? 'bg-mint text-black rounded-br-sm'
              : 'bg-white border border-gray-200 text-navy rounded-bl-sm'
          }`}>
            {msg.message}
          </div>
        </div>
      ))}
      {loading && (
        <div className="flex justify-start">
          <div className="bg-white border border-gray-200 px-4 py-2 rounded-2xl rounded-bl-sm">
            <div className="flex gap-1">
              <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
              <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
              <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
            </div>
          </div>
        </div>
      )}
      <div ref={messagesEndRef} />
    </div>
  );
}

function ChatInput({ value, onChange, onSend, disabled }) {
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      onSend();
    }
  };

  return (
    <div className="p-3 border-t border-gray-200 bg-white">
      <div className="flex gap-2">
        <Input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder="Type your message..."
          className="flex-1"
          data-testid="chat-input"
        />
        <Button
          onClick={onSend}
          disabled={disabled}
          className="bg-mint hover:bg-mint-dark text-black"
          data-testid="chat-send-btn"
        >
          <Send className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

export function ChatWidget() {
  const { api } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async () => {
    if (!inputValue.trim() || loading) return;

    const userMessage = {
      id: Date.now(),
      message: inputValue,
      is_from_user: true,
      created_at: new Date().toISOString(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputValue('');
    setLoading(true);

    try {
      const { data } = await api.post('/api/chat', { message: inputValue, is_from_user: true });
      if (data.reply) {
        setMessages((prev) => [...prev, data.reply]);
      }
    } catch {
      setMessages((prev) => [
        ...prev,
        { id: Date.now() + 1, message: 'Sorry, something went wrong. Please try again.', is_from_user: false, created_at: new Date().toISOString() },
      ]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="chat-widget" style={{ zIndex: 9999 }}>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.9 }}
            className="absolute bottom-20 right-0 w-80 sm:w-96 bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden"
            data-testid="chat-window"
          >
            <ChatHeader onClose={() => setIsOpen(false)} />
            <ChatMessages messages={messages} loading={loading} messagesEndRef={messagesEndRef} />
            <ChatInput
              value={inputValue}
              onChange={setInputValue}
              onSend={sendMessage}
              disabled={!inputValue.trim() || loading}
            />
          </motion.div>
        )}
      </AnimatePresence>

      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => setIsOpen(!isOpen)}
        className="w-14 h-14 rounded-full bg-coral text-white shadow-lg flex items-center justify-center hover:bg-coral-dark transition-colors"
        data-testid="chat-toggle-btn"
      >
        {isOpen ? <X className="h-6 w-6" /> : <MessageCircle className="h-6 w-6" />}
      </motion.button>
    </div>
  );
}
